import { getRightSaluation } from "./function";

const DISCOUNT_UPDATED_ACTION = "Discount updated";

const getPaymentLogAction = (eventName = "") => {
  const name = String(eventName).toLowerCase();
  if (name.includes("payment discount updated")) {
    return DISCOUNT_UPDATED_ACTION;
  }
  if (name.includes("reopen")) return "Reopened";
  if (name.includes("closing") || name === "closed" || name === "close") {
    return "Closed";
  }
  if (name.includes("delete")) return "Deleted";
  if (name.includes("update")) return "Updated";
  if (name.includes("create") || name.includes("newly created")) {
    return "Added";
  }
  return eventName || "-";
};

const isBillLifecycleEvent = (eventName = "") => {
  const name = String(eventName).toLowerCase();
  return (
    name.includes("reopen") ||
    name.includes("closing") ||
    name === "closed" ||
    name === "close"
  );
};

const formatLogMoney = (value) => {
  if (value == null || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : String(value);
};

const getPaymentLogChanges = (param = {}, action = "", previous = null) => {
  if (action === "Closed" || action === "Reopened") {
    return "-";
  }

  if (action === DISCOUNT_UPDATED_ACTION) {
    const parts = [];
    const oldTotal = formatLogMoney(param?.oldTotal);
    const newTotal = formatLogMoney(param?.totalAmt);
    const oldTax = formatLogMoney(param?.oldTax);
    const newTax = formatLogMoney(param?.tax);

    if (oldTotal != null && newTotal != null) {
      parts.push(`Total ${oldTotal} → ${newTotal}`);
    } else if (newTotal != null) {
      parts.push(`Total ${newTotal}`);
    }

    if (oldTax != null && newTax != null) {
      parts.push(`Tax ${oldTax} → ${newTax}`);
    } else if (newTax != null) {
      parts.push(`Tax ${newTax}`);
    }

    return parts.length ? parts.join(", ") : "-";
  }

  const parts = [];
  const name = param?.name || previous?.name || "";
  if (name) parts.push(name);

  const currQty = param?.quantity;
  const prevQty = previous?.quantity;
  const currPrice = formatLogMoney(param?.price);
  const prevPrice = formatLogMoney(previous?.price);

  if (action === "Updated" && previous) {
    if (
      currQty != null &&
      currQty !== "" &&
      prevQty != null &&
      prevQty !== "" &&
      String(prevQty) !== String(currQty)
    ) {
      parts.push(`Qty ${prevQty} → ${currQty}`);
    }
    if (currPrice != null && prevPrice != null && prevPrice !== currPrice) {
      parts.push(`Price ${prevPrice} → ${currPrice}`);
    }
    if (parts.length === (name ? 1 : 0)) {
      if (currQty != null && currQty !== "") parts.push(`Qty ${currQty}`);
      if (currPrice != null) parts.push(`Price ${currPrice}`);
    }
  } else {
    if (currQty != null && currQty !== "") parts.push(`Qty ${currQty}`);
    if (currPrice != null) parts.push(`Price ${currPrice}`);
  }

  return parts.length ? parts.join(", ") : "-";
};

export const flattenPaymentErrorLogs = (
  data = [],
  clinicVets = [],
  clinic = {},
  billDetails = null,
  closingTime = null,
) => {
  const rows = [];
  const resolvePerformerName = (performerId) => {
    if (performerId == null || performerId === "") return "-";
    const idStr = String(performerId);

    const fromVet = (clinicVets ?? []).find(
      (v) =>
        String(v?.doctorId) === idStr ||
        String(v?.userId) === idStr ||
        String(v?.id) === idStr,
    );
    if (fromVet) {
      const salutation = getRightSaluation(
        clinicVets,
        fromVet?.doctorId ?? fromVet?.userId ?? fromVet?.id,
      );
      return `${salutation}${fromVet?.name || ""}`.trim() || "-";
    }

    if (
      String(clinic?.userId) === idStr ||
      String(clinic?.id) === idStr ||
      String(clinic?.createdBy) === idStr ||
      String(clinic?.ownerId) === idStr
    ) {
      return (
        clinic?.userName ||
        clinic?.ownerName ||
        clinic?.name ||
        clinic?.clinicName ||
        `User ${idStr}`
      );
    }

    return `User ${idStr}`;
  };

  const allParams = [];
  (Array.isArray(data) ? data : []).forEach((event) => {
    const params = Array.isArray(event?.params) ? event.params : [];
    const eventName = event?.eventName || "";
    params.forEach((param) => {
      const resolvedEventName = param?.eventName || eventName;
      const isDiscountUpdate = String(resolvedEventName)
        .toLowerCase()
        .includes("payment discount updated");
      if (
        (param?.paymentDetailId == null || param?.paymentDetailId === "") &&
        !isDiscountUpdate &&
        !isBillLifecycleEvent(resolvedEventName)
      ) {
        return;
      }
      allParams.push({
        ...param,
        eventId: event?.eventId,
        eventCreateDate: event?.createDate,
        eventName: resolvedEventName,
        oldTotal: param?.oldTotal ?? event?.oldTotal,
        totalAmt: param?.totalAmt ?? event?.totalAmt,
        oldTax: param?.oldTax ?? event?.oldTax,
        tax: param?.tax ?? event?.tax,
      });
    });
  });

  allParams.sort((a, b) => {
    const aTime = a?.createdDate ? new Date(a.createdDate).getTime() : 0;
    const bTime = b?.createdDate ? new Date(b.createdDate).getTime() : 0;
    return aTime - bTime;
  });

  const lastByDetailId = {};

  allParams.forEach((param, index) => {
    const detailId =
      param?.paymentDetailId != null && param?.paymentDetailId !== ""
        ? String(param.paymentDetailId)
        : `discount_${param?.eventId || index}`;
    const previous = lastByDetailId[detailId] || null;
    const action = getPaymentLogAction(param?.eventName);
    const performerId = param?.loginUser ?? param?.loginUserId ?? null;

    rows.push({
      key: `${param?.eventId || "evt"}_${detailId}_${index}_${param?.createdDate || ""}`,
      createdDate: param?.createdDate || param?.eventCreateDate || null,
      action,
      changes: getPaymentLogChanges(param, action, previous),
      performedBy: resolvePerformerName(performerId),
      paymentDetailId: param?.paymentDetailId ?? null,
      eventName: param?.eventName || "",
      isOpening: false,
    });

    if (param?.paymentDetailId != null && param?.paymentDetailId !== "") {
      lastByDetailId[detailId] = param;
    }
  });

  if (billDetails?.createdDate) {
    const openingPerformerId =
      billDetails?.billedById ?? billDetails?.createdBy ?? null;
    rows.push({
      key: `opening_${billDetails?.paymentId || billDetails?.createdDate}`,
      createdDate: billDetails.createdDate,
      action: "Opening",
      changes: "-",
      performedBy:
        billDetails?.billedByName ||
        resolvePerformerName(openingPerformerId) ||
        "-",
      paymentDetailId: null,
      eventName: "Opening",
      isOpening: true,
    });
  }

  if (closingTime) {
    rows.push({
      key: `closing_${closingTime}`,
      createdDate: closingTime,
      action: "Closed",
      changes: "-",
      performedBy: "-",
      paymentDetailId: null,
      eventName: "Closing",
      isOpening: false,
      isClosing: true,
    });
  }

  return rows.sort((a, b) => {
    const aTime = a?.createdDate ? new Date(a.createdDate).getTime() : 0;
    const bTime = b?.createdDate ? new Date(b.createdDate).getTime() : 0;
    return bTime - aTime;
  });
};
